import { createModel } from '@rematch/core';
import Axios from 'axios';
import PaymentTypes from '../../CurrencyPay/PaymentTypes';

export enum PaymentTermStatus {
  Unknown = 0,
  Unpaid = 1,
  Due = 2,
  Paid = 3,
  Pending = 4,
  PaymentUnsuccessful = 5,
  UnderReview = 6
}

export type Term = {
  termOrder: number;
  dueUpon: string;
  dueDate: Date;
  sellPrice: number;
  percentPrice: number;
  checked: boolean;
  currencyPayStatus: string;
  paymentStatus: PaymentTermStatus;
  paymentType: PaymentTypes;
  changedAtUTC: Date;
};

type PaymentTerms = {
  isFirstTermRequired: boolean;
  remainingTotal: number;
  selectedPaymentsTotal: number;
  quoteTotal: number;
  terms: Term[];
};

const sumSellPrices = function sumSellPrices(terms: Term[]): number {
  return terms
    .filter(t => t.paymentStatus !== PaymentTermStatus.PaymentUnsuccessful)
    .map(t => t.sellPrice)
    .reduce((currentTotal, price) => currentTotal + price, 0);
};

const calculateRemainingTotal = function calculateRemainingTotal(
  terms: Term[]
): number {
  const termsThatCanBePaid = terms.filter(
    t =>
      t.paymentStatus === PaymentTermStatus.Unpaid ||
      t.paymentStatus === PaymentTermStatus.Due ||
      t.paymentStatus === PaymentTermStatus.Unknown
  );
  return sumSellPrices(termsThatCanBePaid.filter(t => !t.checked));
};

const calculateSelectedPaymentsTotal = function calculateSelectedPaymentsTotal(
  terms: Term[]
): number {
  return sumSellPrices(terms.filter(t => t.checked));
};

const setPaymentTerms = function setPaymentTerms(
  state: PaymentTerms,
  terms: Term[]
): PaymentTerms {
  const checkedTerms = terms.map(t => ({
    ...t,
    checked: t.paymentStatus === PaymentTermStatus.Due
  }));
  const remainingTotal = calculateRemainingTotal(checkedTerms);
  const selectedPaymentsTotal = calculateSelectedPaymentsTotal(checkedTerms);
  const quoteTotal = sumSellPrices(terms);
  const isFirstTermRequired = terms.some(
    t => t.termOrder === 0 && t.paymentStatus === PaymentTermStatus.Due
  );

  return {
    ...state,
    quoteTotal,
    remainingTotal,
    selectedPaymentsTotal,
    isFirstTermRequired,
    terms: checkedTerms
  };
};

const canToggleTerm = function canToggleTerm(term: Term): boolean {
  return (
    term.paymentStatus === PaymentTermStatus.Unpaid ||
    term.paymentStatus === PaymentTermStatus.Due
  );
};

const toggleSelectedTerm = function handleToggleSelectedTerm(
  state: PaymentTerms,
  termOrder: number
): PaymentTerms {
  const termToToggle = state.terms.find(
    t => t.termOrder === termOrder && canToggleTerm(t)
  );

  if (!termToToggle) {
    return state;
  }
  const termIndex = state.terms.indexOf(termToToggle);
  const toggledTerm = { ...termToToggle, checked: !termToToggle.checked };

  const allTerms = [
    ...state.terms.slice(0, termIndex),
    toggledTerm,
    ...state.terms.slice(termIndex + 1)
  ];
  const remainingTotal = calculateRemainingTotal(allTerms);
  const selectedPaymentsTotal = calculateSelectedPaymentsTotal(allTerms);

  return { ...state, remainingTotal, selectedPaymentsTotal, terms: allTerms };
};

type SetPaymentTermsStatusPayload = {
  terms: number[];
  status: PaymentTermStatus;
};

const setPaymentTermsStatus = function handleSetPaymentTermsStatus(
  state: PaymentTerms,
  { terms, status }: SetPaymentTermsStatusPayload
): PaymentTerms {
  const updatedTerms = state.terms.map(t => ({
    ...t,
    paymentStatus: terms.some(tu => tu === t.termOrder)
      ? status
      : t.paymentStatus
  }));

  return { ...state, terms: updatedTerms };
};

const paymentTerms = createModel({
  state: {
    isFirstTermRequired: false,
    selectedPaymentsTotal: 0,
    quoteTotal: 0,
    remainingTotal: 0,
    terms: []
  },
  reducers: {
    setPaymentTerms,
    setPaymentTermsStatus,
    toggleSelectedTerm
  },
  effects: {
    async getPaymentTerms(quoteId: string): Promise<void> {
      const loadingForm: any = document.getElementById('loadingInd'); // eslint-disable-line
      loadingForm.showModal();
      const axiosApiInstance = Axios.create({
        baseURL: window.AutoQuotes.config.apiBaseUrl
      });
      try {
        const { data } = await axiosApiInstance.get(
          `quote/${quoteId}/paymentTerms`
        );
        this.setPaymentTerms(data);
      } catch {
        this.setPaymentTerms([]);
      }
      loadingForm.close();
    }
  }
});
export default paymentTerms;
